import React from "react";
import {
    Calendar,
    MapPin,
    Users,
    Trophy,
    ExternalLink,
    CalendarPlus,
    Download,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "./ui/dropdown-menu";
import { googleCalendarUrl, downloadIcs } from "../lib/calendar";

const TYPE_ICONS = {
    League: Users,
    Tournament: Trophy,
    Camp: MapPin,
    Clinic: Users,
};

function formatDateParts(iso) {
    if (!iso) return { day: "--", month: "---" };
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return { day: "--", month: "---" };
    return {
        day: String(d.getDate()).padStart(2, "0"),
        month: d
            .toLocaleString("en-US", { month: "short" })
            .toUpperCase(),
        year: d.getFullYear(),
    };
}

export default function EventCard({ event, index = 0 }) {
    const dp = formatDateParts(event.start_date);
    const Icon = TYPE_ICONS[event.event_type] || Trophy;
    const hasUrl = !!event.url;

    const openRegister = () => {
        if (hasUrl) window.open(event.url, "_blank", "noopener,noreferrer");
    };

    return (
        <div
            data-testid={`event-card-${event.id}`}
            onClick={openRegister}
            role={hasUrl ? "button" : undefined}
            tabIndex={hasUrl ? 0 : undefined}
            onKeyDown={(e) => {
                if (hasUrl && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    openRegister();
                }
            }}
            className={[
                "group flex gap-4 border border-white/10 bg-[#111111] p-4 transition-all duration-300 fade-up focus:outline-none focus:border-[#FF3B30]",
                hasUrl
                    ? "cursor-pointer hover:border-[#FF3B30] hover:translate-x-1"
                    : "hover:border-white/20",
            ].join(" ")}
            style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
        >
            {event.image_url && (
                <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ""}${event.image_url}`}
                    alt=""
                    className="w-16 h-16 object-cover flex-shrink-0 border border-white/10"
                    loading="lazy"
                    data-testid={`event-image-${event.id}`}
                />
            )}
            <div className="flex flex-col items-center justify-center min-w-[64px] bg-white/[0.03] border border-white/10 py-2 px-3">
                <div className="font-anton text-3xl leading-none text-white">
                    {dp.day}
                </div>
                <div className="text-[10px] tracking-[0.2em] text-zinc-400 mt-1">
                    {dp.month}
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] bg-[#FF3B30]/15 text-[#FF3B30]">
                        {event.event_type}
                    </span>
                    <span className="text-[9px] text-zinc-500 tracking-[0.2em] uppercase">
                        {event.gender} · {(event.age_ranges || (event.age_range ? [event.age_range] : [])).join(" · ")}
                    </span>
                </div>
                <h3 className="font-anton text-xl text-white uppercase leading-tight mb-1 tracking-wide truncate group-hover:text-[#FF3B30] transition-colors">
                    {event.title}
                    {hasUrl && (
                        <ExternalLink
                            size={14}
                            className="inline-block ml-2 align-baseline opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                    )}
                </h3>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400 font-manrope">
                    <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {event.county}
                    </span>
                    {event.venue && (
                        <span className="inline-flex items-center gap-1">
                            <Icon size={12} />
                            {event.venue}
                        </span>
                    )}
                    {event.end_date && event.end_date !== event.start_date && (
                        <span className="inline-flex items-center gap-1">
                            <Calendar size={12} />
                            through{" "}
                            {new Date(
                                `${event.end_date}T00:00:00`,
                            ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                            })}
                        </span>
                    )}
                </div>
                {event.description && (
                    <p className="text-xs text-zinc-500 mt-2 line-clamp-2">
                        {event.description}
                    </p>
                )}

                <div
                    className="flex items-center gap-3 mt-2.5 flex-wrap"
                    onClick={(e) => e.stopPropagation()}
                >
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/80 hover:text-[#FF3B30] border border-white/15 hover:border-[#FF3B30] px-2.5 py-1 transition-colors"
                                data-testid={`add-to-calendar-${event.id}`}
                            >
                                <CalendarPlus size={11} /> Add to calendar
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            className="rounded-none bg-[#0f0f0f] border-white/15 text-white font-manrope min-w-[200px]"
                        >
                            <DropdownMenuItem
                                asChild
                                className="rounded-none focus:bg-[#FF3B30]/20 focus:text-white cursor-pointer"
                            >
                                <a
                                    href={googleCalendarUrl(event)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`gcal-${event.id}`}
                                    className="text-xs uppercase tracking-[0.18em]"
                                >
                                    <ExternalLink size={12} />
                                    Google Calendar
                                </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => downloadIcs(event)}
                                className="rounded-none focus:bg-[#FF3B30]/20 focus:text-white cursor-pointer text-xs uppercase tracking-[0.18em]"
                                data-testid={`ical-${event.id}`}
                            >
                                <Download size={12} />
                                Apple / Outlook (.ics)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {event.url && (
                        <a
                            href={event.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] hover:text-white"
                        >
                            Details <ExternalLink size={10} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}
